import { ChevronRight } from "lucide-react";
import { Fragment } from "react";
import { FieldGrid } from "@/components/contracts/FieldGrid";
import { formatCellValue } from "@/components/contracts/table-utils";
import { DataTable } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { PrivateHireMovement } from "@/lib/coach-manager/types";
import { cn } from "@/lib/utils";
import { PrivateHireDataValuesTable } from "./PrivateHireDataValuesTable";
import { PrivateHireExtrasTable } from "./PrivateHireExtrasTable";
import { PrivateHireFacilitiesTable } from "./PrivateHireFacilitiesTable";
import { PrivateHireIncludedItemsTable } from "./PrivateHireIncludedItemsTable";
import { PrivateHirePickupsTable } from "./PrivateHirePickupsTable";
import { PrivateHireSetdownsTable } from "./PrivateHireSetdownsTable";
import { PrivateHireVehiclesTable } from "./PrivateHireVehiclesTable";

interface PrivateHireMovementsTableProps {
  movements: PrivateHireMovement[];
  /** Which movement's accordion row (field grid + nested tabs) is open. Only one at a time. */
  expandedMovementId: number | null;
  onToggle: (movement: PrivateHireMovement) => void;
}

function movementSummary(movement: PrivateHireMovement): string {
  return (
    movement.MovementDescription ||
    [movement.Pickup, movement.Destination].filter(Boolean).join(" → ") ||
    `Movement ${movement.PrivateHireMovementID}`
  );
}

/**
 * A movement's nested tables — vehicles/drivers, pickups, setdowns,
 * facilities, included items, extras, and its own data — as pill tab
 * toggles, one category visible at a time. Only categories with data get a
 * tab. Mirrors Contract's `MovementNestedTables`, plus the **Extras**
 * category Contract's Movement doesn't have.
 */
function PrivateHireMovementNestedTables({ movement }: { movement: PrivateHireMovement }) {
  const categories = [
    {
      value: "vehicles",
      label: "Vehicles",
      count: movement.PrivateHireVehicles?.length ?? 0,
      render: () => <PrivateHireVehiclesTable vehicles={movement.PrivateHireVehicles ?? []} />,
    },
    {
      value: "pickups",
      label: "Pickups",
      count: movement.PrivateHirePickups?.length ?? 0,
      render: () => <PrivateHirePickupsTable pickups={movement.PrivateHirePickups ?? []} />,
    },
    {
      value: "setdowns",
      label: "Setdowns",
      count: movement.PrivateHireSetdowns?.length ?? 0,
      render: () => <PrivateHireSetdownsTable setdowns={movement.PrivateHireSetdowns ?? []} />,
    },
    {
      value: "facilities",
      label: "Facilities",
      count: movement.PrivateHireFacilities?.length ?? 0,
      render: () => (
        <PrivateHireFacilitiesTable facilities={movement.PrivateHireFacilities ?? []} />
      ),
    },
    {
      value: "includedItems",
      label: "Included Items",
      count: movement.PrivateHireIncludedItems?.length ?? 0,
      render: () => (
        <PrivateHireIncludedItemsTable items={movement.PrivateHireIncludedItems ?? []} />
      ),
    },
    {
      value: "extras",
      label: "Extras",
      count: movement.PrivateHireExtras?.length ?? 0,
      render: () => <PrivateHireExtrasTable extras={movement.PrivateHireExtras ?? []} />,
    },
    {
      value: "movementData",
      label: "Movement Data",
      count: movement.PrivateHireMovementData?.length ?? 0,
      render: () => <PrivateHireDataValuesTable rows={movement.PrivateHireMovementData ?? []} />,
    },
  ].filter((category) => category.count > 0);

  if (categories.length === 0) {
    return <p className="text-sm text-muted-foreground">This movement has no nested data.</p>;
  }

  return (
    <Tabs defaultValue={categories[0]?.value}>
      <TabsList>
        {categories.map((category) => (
          <TabsTrigger key={category.value} value={category.value}>
            {category.label} ({category.count})
          </TabsTrigger>
        ))}
      </TabsList>
      {categories.map((category) => (
        <TabsContent key={category.value} value={category.value}>
          {category.render()}
        </TabsContent>
      ))}
    </Tabs>
  );
}

/**
 * Full-width movements table: critical columns only, plus one expandable
 * accordion row per movement containing its full field grid AND its nested
 * category tabs together, inline. Only one movement's accordion is open at a
 * time. Mirrors `MovementsTable` exactly. `PrivateHireMovement`'s date/time
 * fields are combined `*DateTime` strings on the wire (not separate
 * Day(int)+Time(string) pairs like `ContractMovement`), so the field grid
 * below reflects that shape.
 */
export function PrivateHireMovementsTable({
  movements,
  expandedMovementId,
  onToggle,
}: PrivateHireMovementsTableProps) {
  return (
    <DataTable
      headers={[
        { label: "", className: "w-8" },
        { label: "Movement" },
        { label: "Pickup" },
        { label: "Passengers" },
        { label: "Vehicles" },
        { label: "Pickups" },
        { label: "Setdowns" },
        { label: "Facilities" },
        { label: "Status" },
      ]}
      empty={<span className="text-sm text-muted-foreground">No movements.</span>}
    >
      {movements.map((movement) => {
        const isExpanded = expandedMovementId === movement.PrivateHireMovementID;
        return (
          <Fragment key={movement.PrivateHireMovementID}>
            <tr
              className={cn(
                "cursor-pointer border-b last:border-b-0 hover:bg-muted/40",
                isExpanded && "bg-accent",
              )}
              onClick={() => onToggle(movement)}
            >
              <td className="px-2 py-2">
                <ChevronRight
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform",
                    isExpanded && "rotate-90",
                  )}
                />
              </td>
              <td className="px-4 py-2">{movementSummary(movement)}</td>
              <td className="px-4 py-2">{formatCellValue(movement.PickupDateTime)}</td>
              <td className="px-4 py-2">{movement.Passengers}</td>
              <td className="px-4 py-2">{movement.PrivateHireVehicles?.length || "—"}</td>
              <td className="px-4 py-2">{movement.PrivateHirePickups?.length || "—"}</td>
              <td className="px-4 py-2">{movement.PrivateHireSetdowns?.length || "—"}</td>
              <td className="px-4 py-2">{movement.PrivateHireFacilities?.length || "—"}</td>
              <td className="px-4 py-2">
                {movement.Cancelled ? (
                  <Badge variant="destructive">Cancelled</Badge>
                ) : (
                  <Badge variant="success">Active</Badge>
                )}
              </td>
            </tr>
            {isExpanded && (
              <tr className="border-b bg-background last:border-b-0">
                <td colSpan={9} className="px-4 py-3">
                  <div className="space-y-4">
                    <FieldGrid
                      fields={[
                        { label: "Movement ID", value: movement.PrivateHireMovementID },
                        { label: "Movement Description", value: movement.MovementDescription },
                        { label: "Start Base Serial No", value: movement.StartBaseSerialNo },
                        { label: "Start Date/Time", value: movement.StartDateTime },
                        { label: "Position Date/Time", value: movement.PositionDateTime },
                        { label: "Pickup Date/Time", value: movement.PickupDateTime },
                        { label: "Pickup", value: movement.Pickup },
                        { label: "Pickup Latitude", value: movement.PickupLatitude },
                        { label: "Pickup Longitude", value: movement.PickupLongitude },
                        { label: "Pickup Instructions", value: movement.PickupInstructions },
                        { label: "Single Journey", value: movement.SingleJourney },
                        { label: "Vehicle To Stay", value: movement.VehicleToStay },
                        { label: "Destination", value: movement.Destination },
                        { label: "Destination Latitude", value: movement.DestinationLatitude },
                        { label: "Destination Longitude", value: movement.DestinationLongitude },
                        {
                          label: "Destination Instructions",
                          value: movement.DestinationInstructions,
                        },
                        { label: "Arrival Date/Time", value: movement.ArrivalDateTime },
                        { label: "Leave Date/Time", value: movement.LeaveDateTime },
                        { label: "Back Date/Time", value: movement.BackDateTime },
                        { label: "Finish Date/Time", value: movement.FinishDateTime },
                        { label: "Finish Base Serial No", value: movement.FinishBaseSerialNo },
                        { label: "Passengers", value: movement.Passengers },
                        { label: "Distance", value: movement.Distance },
                        { label: "Empty Distance", value: movement.EmptyDistance },
                        { label: "Client Reference 1", value: movement.ClientReference1 },
                        { label: "Client Reference 2", value: movement.ClientReference2 },
                        { label: "Route", value: movement.Route },
                        { label: "Further Requirements", value: movement.FurtherRequirements },
                        { label: "Notes", value: movement.Notes },
                        { label: "Movement Price", value: movement.PrivateHireMovementPrice },
                        { label: "Movement VAT", value: movement.PrivateHireMovementVAT },
                        { label: "Movement Total", value: movement.PrivateHireMovementTotal },
                        { label: "Date Created", value: movement.DateCreated },
                        { label: "Last Updated", value: movement.LastUpdated },
                      ]}
                    />
                    <PrivateHireMovementNestedTables movement={movement} />
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </DataTable>
  );
}
